import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationManager } from 'react-notifications';

import HotelLogo from '../components/common/HotelLogo';
import SectionHeader from '../components/common/SectionHeader';
import HorizontalDivider from '../components/common/HorizontalDivider';
import Text from '../components/common/Text';
import Overlay from '../components/common/Overlay';
import Button from '../components/common/Button';
import ProcessingPaymentSpinner from '../components/common/ProcessingPaymentSpinner';
import PaymentMethodSelector from '../components/payment/PaymentMethodSelector';
import CreditCardOption from '../components/payment/CreditCardOption';
import {
  addTransitoryCharge,
  createInvoice,
  createPaymentSession,
  getFoliosOfReservation,
  getPaymentMethods,
  getReservation,
  makePaymentByAccount,
  makePaymentByAuthorization,
  sendEvent,
} from '../redux/reducer/actions';

const PaymentPage = () => {
  const { uniqueId, propertyId, selectedItemId } = useParams();
  const [selectedPaymentOption, setSetselectedPaymentOption] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const reservation = useSelector((state) => state.app.reservation).data;
  const currentTip = useSelector((state) => state.app.currentTip).data;
  const currentFolioIds = useSelector(
    (state) => state.app.currentFolioIds,
  ).data;
  const paymentMethodsObject = useSelector(
    (state) => state.app.paymentMethod.list,
  );
  const paymentSession = useSelector((state) => state.app.paymentSession);
  const reservationFolios = useSelector((state) => state.app.reservationFolios);
  const { invoicesPageUrl, billingAddressPageUrl } = useSelector(
    (state) => state.app.urls,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [foliosToBePaid, setFoliosToBePaid] = useState(null);
  const [totalGrossAmount, setTotalGrossAmount] = useState(0);
  useEffect(() => {
    dispatch(getPaymentMethods({ hotelId: uniqueId }));
  }, [dispatch, uniqueId]);

  useEffect(() => {
    if (reservationFolios) {
      const foliosToBePaid = currentFolioIds.map((folioId) => reservationFolios.find((folio) => folio.id === folioId));
      if (foliosToBePaid.some((folio) => !folio)) {
        console.error('Some folios could not be found in reservationFolios');
        return;
      }
      setFoliosToBePaid(foliosToBePaid);
    }
  }, [currentFolioIds, reservationFolios]);

  useEffect(() => {
    if (foliosToBePaid) {
      const total = Math.abs(
        foliosToBePaid.reduce(
          (total, item) => total + (item.balance?.amount || 0),
          0,
        ),
      );
      setTotalGrossAmount(total);
    }
  }, [foliosToBePaid]);

  useEffect(() => {
    if (totalGrossAmount) {
      dispatch(
        createPaymentSession({
          hotelId: uniqueId,
          amount: { value: totalGrossAmount * 100, currency: 'EUR' },
        }),
      );
    }
  }, [uniqueId, dispatch, totalGrossAmount]);

  useEffect(() => {
    dispatch(
      getFoliosOfReservation({
        uniqueId,
        hotelId: uniqueId,
        reservationId: reservation.id,
      }),
    );
  }, [dispatch, uniqueId, reservation]);

  const fireResultSuccess = () => {
    dispatch(
      sendEvent({
        roomId: reservation.property.code,
        reservationId: reservation.id,
        type: 'PAYMENT_SUCCESSFUL',
      }),
    );
    setTimeout(async () => {
      const invoiceResult = await dispatch(
        createInvoice({ hotelId: uniqueId, folios: foliosToBePaid }),
      );

      if (invoiceResult && invoiceResult.payload) {
        NotificationManager.success(
          'Success! Payment Successful... Redirecting',
          'Notification',
          3000,
        );
        dispatch(
          getReservation({
            uniqueId,
            hotelId: propertyId,
            roomId: selectedItemId,
          }),
        );
        setPaymentCompleted(true);
        navigate(invoicesPageUrl);
      }
    }, 7000);
  };

  const fireAuthenticationPayment = async (values, amount) => {
    try {
      const paymentResult = await dispatch(
        makePaymentByAuthorization({
          hotelId: uniqueId,
          tip: currentTip,
          paymentMethod: values.data.paymentMethod.type,
          folios: JSON.stringify(foliosToBePaid),
          amount,
        }),
      );
      if (paymentResult && paymentResult.payload) {
        fireResultSuccess();
      }
    } catch (error) {
      NotificationManager.error(
        'Error during payment',
        error,
        'Notification',
        3000,
      );
    }
  };

  const handleAccountPay = async () => {
    setIsProcessingPayment(true);
    if (currentTip) {
      await dispatch(
        addTransitoryCharge({
          hotelId: uniqueId,
          folioIds: currentFolioIds.join(),
          tip: currentTip,
          receipt: reservation.id,
        }),
      );
    }
    if (reservationFolios) {
      try {
        const paymentResult = await dispatch(
          makePaymentByAccount({
            hotelId: uniqueId,
            folioIds: foliosToBePaid,
            tip: currentTip,
          }),
        );

        if (paymentResult && paymentResult.payload) {
          fireResultSuccess();
        }
      } catch (error) {
        NotificationManager.error(
          'Error during payment or invoice creation:',
          error,
          'Notification',
          3000,
        );
        dispatch(
          sendEvent({
            roomId: reservation.property.code,
            reservationId: reservation.id,
            type: 'PAYMENT_FAILED',
          }),
        );
      }
    }
  };
  const handleAuthenticationPay = async (values) => {
    if (values) {
      setTotalGrossAmount((prevTotal) => {
        fireAuthenticationPayment(values, prevTotal);
        return prevTotal;
      });
    }
  };

  return (
    <main className="flex min-h-screen flex-col gap-16 py-16">
      <HotelLogo />
      <section>
        <SectionHeader text="Choose a payment" />
        <div className="flex flex-col gap-6 py-6">
          {reservation?.paymentAccount && (
          <div>
            <Text>Use the credit card provided at check-in.</Text>
            <CreditCardOption
              paymentAccount={reservation?.paymentAccount}
              selectedPaymentOption={selectedPaymentOption}
              setSelectedPaymentOption={setSetselectedPaymentOption}
            />
            <HorizontalDivider text="or" />
          </div>
          )}
        </div>
        {paymentMethodsObject
          && paymentMethodsObject.paymentMethods
          && paymentMethodsObject.paymentMethods.length > 0 && (
            <div>
              <div className="">
                <Text>Use a new payment method</Text>
                <PaymentMethodSelector
                  paymentMethods={paymentMethodsObject}
                  paymentSession={paymentSession}
                  total={totalGrossAmount}
                  selectedPaymentOption={selectedPaymentOption}
                  setSelectedPaymentOption={setSetselectedPaymentOption}
                  onSubmit={(values) => handleAuthenticationPay(values).then(() => console.log('Authentication Pay Started'))}
                />
              </div>
            </div>
        )}

        <div className="flex flex-col gap-6 xxs:flex-row">
          <Button
            variant="dark"
            text="Go back"
            type="button"
            onClick={() => {
              navigate(billingAddressPageUrl);
            }}
          />
          <Button
            variant="primary"
            text="Continue"
            type="button"
            disabled={selectedPaymentOption === null}
            onClick={() => {
              NotificationManager.info('Payment started', 'Notification', 3000);
              handleAccountPay().then((r) => console.log('Account Payment started', r));
            }}
          />
        </div>
      </section>
      {isProcessingPayment && (
        <div className="fixed inset-x-0 top-0 flex h-screen w-screen flex-col items-center justify-center px-4">
          {/* z-10 */}
          <Overlay onEscape={() => {}} />

          {/* z-20 */}
          <section
            className="z-20 flex max-h-[80vh] min-h-40 w-[85vw] min-w-72 max-w-md  flex-col gap-5 rounded-lg bg-white p-6 shadow-pj-xl"
            role="dialog"
            aria-modal="true"
          >
            {paymentCompleted ? (
              <div className="flex flex-col gap-4">
                <SectionHeader text="Success!" center />
                <div className="custom-scrollbar mb-6 max-h-[60vh] overflow-auto">
                  <Text>Your payment was processed successfully.</Text>
                </div>
                <Button
                  variant="primary"
                  text="Continue"
                  type="button"
                  disabled={selectedPaymentOption === null}
                  onClick={() => {
                    navigate(invoicesPageUrl);
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <SectionHeader text="Processing payment" center />
                <div className="custom-scrollbar max-h-[60vh] overflow-auto">
                  <Text center>
                    Please wait while we process your payment. This may take a
                    few moments. Do not refresh this page.
                  </Text>
                </div>
                <ProcessingPaymentSpinner />
              </div>
            )}
          </section>
        </div>
      )}
    </main>
  );
};

export default PaymentPage;
